import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useCallback } from 'react';
import {
  VendorKnowledgeScreen,
  type KbView,
} from '@/components/vendor-knowledge/vendor-knowledge-screen';

/**
 * Settings → Vendor knowledge base (T-0206, T-0207; E13).
 *
 * What Caliber knows about each vendor's products: vendors and partners on
 * the left, the selected one on the right. The screen is
 * `components/vendor-knowledge/vendor-knowledge-screen.tsx`; this route
 * only owns the selection in the URL, so a page can be shared and Back
 * works:
 *
 *   ?vendor=<id> | ?partner=<id> | ?view=new | ?view=settings | ?view=lists | ?list=<id>
 *
 * Design reference: `docs/vendor-knowledge/prototype/knowledge.html`.
 */

interface KbSearch {
  vendor?: string;
  partner?: string;
  list?: string;
  view?: 'new' | 'settings' | 'lists';
}

function text(v: unknown): string | undefined {
  return typeof v === 'string' && v.length > 0 ? v : undefined;
}

export const Route = createFileRoute('/settings/vendor-knowledge')({
  validateSearch: (search: Record<string, unknown>): KbSearch => {
    const view = search['view'];
    return {
      vendor: text(search['vendor']),
      partner: text(search['partner']),
      list: text(search['list']),
      view: view === 'new' || view === 'settings' || view === 'lists' ? view : undefined,
    };
  },
  component: VendorKnowledgeRoute,
});

function toView(s: KbSearch): KbView {
  if (s.vendor) return { kind: 'vendor', id: s.vendor };
  if (s.partner) return { kind: 'partner', id: s.partner };
  if (s.list) return { kind: 'list', id: s.list };
  if (s.view === 'new') return { kind: 'new' };
  if (s.view === 'settings') return { kind: 'settings' };
  if (s.view === 'lists') return { kind: 'lists' };
  return { kind: 'home' };
}

function toSearch(v: KbView): KbSearch {
  switch (v.kind) {
    case 'vendor':
      return { vendor: v.id };
    case 'partner':
      return { partner: v.id };
    case 'new':
      return { view: 'new' };
    case 'settings':
      return { view: 'settings' };
    case 'lists':
      return { view: 'lists' };
    case 'list':
      return { list: v.id };
    default:
      return {};
  }
}

function VendorKnowledgeRoute() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const onNavigate = useCallback(
    (next: KbView) => {
      void navigate({ search: toSearch(next) });
    },
    [navigate],
  );
  return (
    // Fills the settings pane: viewport minus the 56px top bar and the
    // settings layout's vertical padding.
    <div className="h-[calc(100vh-56px-48px)] min-h-[480px] overflow-hidden rounded-card border border-ink-100 bg-paper-0">
      <VendorKnowledgeScreen
        view={toView(search)}
        onNavigate={onNavigate}
        onOpenPath={(to) => void navigate({ to })}
      />
    </div>
  );
}
